import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, ChevronLeft, ChevronRight, Quote, Loader2 } from 'lucide-react';

// Star Rating Display
function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star 
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    fetchTestimonials();
  }, []);
  
  const fetchTestimonials = async () => {
    try {
      const response = await axios.get('/api/testimonials/approved?limit=10');
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  // Don't render if no testimonials
  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="container-custom flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </section>
    );
  }
  
  if (testimonials.length === 0) {
    return null; // Hide section if no testimonials
  }
  
  const currentTestimonial = testimonials[currentIndex];
  
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <p className="eyebrow mb-4">What Leaders Are Saying</p>
          <h2 className="h2 mb-4">Real experiences from PATH participants</h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          {/* Main Testimonial Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 relative">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Quote className="w-6 h-6 text-white" />
            </div>
            
            <div className="pt-4">
              {/* Rating */}
              <div className="mb-6">
                <StarRating rating={currentTestimonial.rating} />
              </div>
              
              {/* Testimonial Text */}
              <blockquote className="text-lg md:text-xl text-foreground leading-relaxed mb-8">
                "{currentTestimonial.text}"
              </blockquote>
              
              {/* Author */}
              <div className="flex items-center gap-4">
                {currentTestimonial.photoBase64 ? (
                  <img 
                    src={`data:${currentTestimonial.photoMimeType};base64,${currentTestimonial.photoBase64}`}
                    alt={`${currentTestimonial.firstName}`}
                    className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-white font-semibold text-lg">
                    {currentTestimonial.firstName?.[0]}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-foreground">
                    {currentTestimonial.firstName} {currentTestimonial.lastName}
                  </p>
                  {currentTestimonial.company && (
                    <p className="text-sm text-muted-foreground">{currentTestimonial.company}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          {testimonials.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-10 h-10 rounded-full border border-border bg-white hover:bg-muted flex items-center justify-center transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Dots */}
              <div className="flex items-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'w-6 bg-primary' 
                        : 'bg-border hover:bg-primary/50'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextTestimonial}
                className="w-10 h-10 rounded-full border border-border bg-white hover:bg-muted flex items-center justify-center transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
